// src/pages/Reportes.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import GlassCard from '../components/UI/GlassCard';
import { FileText, Download, Calendar, TrendingUp, Users, Scissors, ChevronRight, AlertCircle, PieChart, BarChart3, Clock, Eye, Printer, X, DollarSign, Scale } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Modal from '../components/UI/Modal';

const Reportes = () => {
    const { can } = useAuth();
    const toLocalISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];

    const [dateRange, setDateRange] = useState({
        start: toLocalISO(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        end: toLocalISO(new Date())
    });

    const [loading, setLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [selectedReportName, setSelectedReportName] = useState('');

    const isMobileDevice = () => {
        return (window.innerWidth < 1024) || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const handlePreview = (report) => {
        const token = localStorage.getItem('token');
        const url = `/backend/api/reportes/${report.id}.php?start=${dateRange.start}&end=${dateRange.end}&token=${token}`;
        
        if (isMobileDevice()) {
            toast.success('Abriendo reporte en visor nativo de la tablet... 🚀');
            window.open(url, '_blank');
            return;
        }

        setPreviewUrl(url);
        setSelectedReportName(report.title);
        setShowPreview(true);
        
        toast.loading('Preparando visualización oficial...', {
            id: 'preview-load',
            style: { background: '#0f172a', color: '#fff', border: '1px solid rgba(139, 92, 246, 0.2)' }
        });

        setTimeout(() => {
            toast.success('Documento cargado correctamente 📄', { id: 'preview-load' });
        }, 1200);
    };

    const handleDownload = () => {
        window.open(previewUrl + '&download=1', '_blank');
    };

    const reportCards = [
        {
            id: 'cierre',
            title: 'Cierre de Caja Operativo',
            desc: 'Desglose detallado de ventas, impuestos (IVU) y métodos de pago por periodo.',
            icon: TrendingUp,
            color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20'
        },
        {
            id: 'rendimiento',
            title: 'Rendimiento de Staff / Groomers',
            desc: 'Analítica de servicios realizados por cada profesional y productividad del equipo.',
            icon: Scissors,
            color: 'bg-primary/20 text-primary-light border-primary/20'
        },
        {
            id: 'finanzas',
            title: 'Salud Financiera & Auditoría',
            desc: 'Resumen ejecutivo de ingresos bruto/neto, impuestos y rentabilidad por categoría.',
            icon: DollarSign,
            color: 'bg-amber-500/20 text-amber-400 border-amber-500/20'
        },
        {
            id: 'ivu_suri',
            title: 'Resumen Fiscal IVU (SURI)',
            desc: 'Reporte auxiliar para la planilla mensual SC-2915. Desglose de ventas exentas vs tributables.',
            icon: Scale,
            color: 'bg-blue-500/20 text-blue-400 border-blue-500/20'
        },
        {
            id: 'clientes_top',
            title: 'Demografía de Clientes VIP',
            desc: 'Ranking de dueños de mascotas con mayor recurrencia y valor de vida (LTV).',
            icon: Users,
            color: 'bg-secondary/20 text-secondary-light border-secondary/20'
        }
    ];

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
                <div>
                    <h1 className="text-4xl lg:text-5xl font-black flex items-center gap-4 text-white">
                        <FileText className="text-primary-light" size={48} /> 
                        Centro de <span className="text-primary-light italic font-medium">Informes</span>
                    </h1>
                    <p className="text-white/40 mt-2 text-sm lg:text-base font-medium flex items-center gap-2">
                        <BarChart3 size={14} className="text-secondary" />
                        Analítica avanzada y reportes institucionales PDF (ISO 9001)
                    </p>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
                {/* Panel de Filtros */}
                <div className="lg:col-span-1 space-y-6">
                    <GlassCard padding="30px" className="border-white/10 shadow-2xl bg-slate-900/40">
                        <h3 className="text-xs font-black uppercase text-white/30 tracking-widest mb-6 flex items-center gap-2 italic">
                            <Clock size={12}/> Filtro Temporal
                        </h3>
                        
                        <div className="space-y-5">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-primary-light tracking-tighter">Desde el día:</label>
                                <input 
                                    type="date" 
                                    value={dateRange.start} 
                                    onChange={e => setDateRange({...dateRange, start: e.target.value})}
                                    className="input-field bg-slate-800/80 [color-scheme:dark]"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-primary-light tracking-tighter">Hasta el día:</label>
                                <input 
                                    type="date" 
                                    value={dateRange.end} 
                                    onChange={e => setDateRange({...dateRange, end: e.target.value})}
                                    className="input-field bg-slate-800/80 [color-scheme:dark]"
                                />
                            </div>

                            <div className="pt-4 grid grid-cols-1 gap-2">
                                <button 
                                    onClick={() => {
                                        const today = toLocalISO(new Date());
                                        setDateRange({ start: today, end: today });
                                        toast.success('Filtro: Hoy');
                                    }}
                                    className="glass py-2 rounded-xl text-[10px] font-black uppercase text-white/40 hover:text-white transition-all"
                                >
                                    Hoy
                                </button>
                                <button 
                                    onClick={() => {
                                        const now = new Date();
                                        const firstDay = toLocalISO(new Date(now.getFullYear(), now.getMonth(), 1));
                                        const lastDay = toLocalISO(new Date(now.getFullYear(), now.getMonth() + 1, 0));
                                        setDateRange({ start: firstDay, end: lastDay });
                                        toast.success('Filtro: Este Mes');
                                    }}
                                    className="glass py-2 rounded-xl text-[10px] font-black uppercase text-white/40 hover:text-white transition-all"
                                >
                                    Este Mes
                                </button>
                            </div>
                        </div>
                    </GlassCard>

                    <div className="p-6 rounded-3xl bg-secondary/5 border border-secondary/10">
                        <PieChart className="text-secondary mb-3" size={20} />
                        <p className="text-xs text-secondary-light/60 font-medium leading-relaxed italic">
                            "Los reportes PDF generados cumplen con los requerimientos de auditoría fiscal para el IVU en Puerto Rico."
                        </p>
                    </div>
                </div>

                {/* Listado de Reportes */}
                <div className="lg:col-span-3 space-y-6">
                    {reportCards.map((report, i) => (
                        <div key={report.id} className="group relative overflow-hidden transition-all hover:-translate-y-1">
                            <div className={`absolute top-0 right-0 w-64 h-64 -mr-16 -mt-16 rounded-full opacity-[0.03] group-hover:opacity-10 transition-all ${report.color}`}></div>
                            <GlassCard padding="35px" className="border-white/5 shadow-2xl relative z-10 flex flex-col md:flex-row items-center gap-8 bg-slate-900/40">
                                <div className={`p-6 rounded-3xl border shadow-lg ${report.color} flex-shrink-0 group-hover:scale-110 transition-transform duration-500`}>
                                    <report.icon size={32} />
                                </div>
                                <div className="flex-1 text-center md:text-left">
                                    <h2 className="text-2xl font-black text-white tracking-tighter mb-2">{report.title}</h2>
                                    <p className="text-white/40 font-medium text-sm leading-relaxed max-w-xl">
                                        {report.desc}
                                    </p>
                                </div>
                                {can('Reportes', 'acceso') && (
                                    <button 
                                        onClick={() => handlePreview(report)}
                                        className="bg-primary/10 hover:bg-primary/20 text-primary-light px-8 py-4 rounded-2xl flex items-center gap-3 font-black text-[12px] uppercase tracking-[2px] transition-all border border-primary/20 hover:scale-105"
                                    >
                                        <Eye size={18} />
                                        Visualizar Informe
                                    </button>
                                )}
                            </GlassCard>
                        </div>
                    ))}

                    <div className="p-8 border-2 border-dashed border-white/5 rounded-[40px] flex flex-col items-center justify-center text-white/10 italic">
                        <AlertCircle size={32} className="mb-4" />
                        <span className="text-sm font-black uppercase tracking-[4px]">Audit Trail Active — ISO-0922</span>
                    </div>
                </div>
            </div>

            <footer className="mt-20 pt-10 border-t border-white/5 flex justify-between items-center opacity-30 italic text-xs font-black tracking-widest text-white uppercase">
                <span>Enterprise Reporting Engine — v4.0</span>
                <span>PawSpa Pro Cloud Analytics</span>
            </footer>

            {/* Modal de Previsualización Premium */}
            <Modal
                show={showPreview}
                onClose={() => {
                    setShowPreview(false);
                    setPreviewUrl('');
                }}
                title={`Centro de Visualización: ${selectedReportName}`}
                size="full"
                footer={(
                    <div className="flex justify-end items-center w-full px-4">
                        <div className="flex gap-4">
                            {can('Reportes', 'exportar') && (
                                <button 
                                    onClick={handleDownload}
                                    className="bg-primary px-10 py-3 rounded-xl font-black text-xs uppercase tracking-[2px] text-white shadow-xl shadow-primary/20 hover:scale-105 transition-all flex items-center gap-2"
                                >
                                    <Download size={16} /> Descargar Archivo Final
                                </button>
                            )}
                        </div>
                    </div>
                )}
            >
                <div className="w-full h-[75vh] glass rounded-[32px] overflow-hidden border border-white/10 bg-slate-900/50 relative">
                    <iframe 
                        src={previewUrl} 
                        className="w-full h-full border-none hidden md:block"
                        title="Premium PDF Preview"
                    ></iframe>
                    
                    {/* Fallback para Tablets/Móviles si llegan a abrir el modal */}
                    <div className="md:hidden flex flex-col items-center justify-center h-full p-10 text-center">
                        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6 border border-primary/20">
                            <FileText size={40} className="text-primary-light" />
                        </div>
                        <h3 className="text-xl font-black text-white mb-4">Visor Móvil Activo</h3>
                        <p className="text-white/40 mb-8 max-w-xs mx-auto text-sm font-medium">Su dispositivo requiere abrir el PDF en una ventana independiente para una visualización óptima.</p>
                        <button 
                            onClick={() => window.open(previewUrl, '_blank')}
                            className="premium-btn-primary px-10 py-4 flex items-center gap-3"
                        >
                            <Printer size={18} /> ABRIR REPORTE AHORA
                        </button>
                    </div>

                    <div className="absolute bottom-6 left-6 bg-slate-950/80 backdrop-blur-md px-4 py-2 rounded-xl border border-white/10 text-[10px] font-black text-primary-light uppercase tracking-widest z-20 shadow-2xl">
                        Entorno de Auditoría Seguro
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Reportes;
